import { useEffect, useMemo, useState } from 'react';
import { Ban, CreditCard, Loader2, Save, Search, Upload, UserRound, Users } from 'lucide-react';
import Modal from './Modal';
import { Checkbox, SearchSelect } from './FieldComponents';

const draftFromMapping = (mapping) => ({
    user_id: mapping?.user_id ? String(mapping.user_id) : '',
    recipient_group_id: mapping?.recipient_group_id ? String(mapping.recipient_group_id) : '',
    receipt_request_exempt: Number(mapping?.receipt_request_exempt) === 1,
    apply_to_existing: true
});

const draftFromGroup = (group) => ({
    name: group?.name || '',
    member_ids: (group?.member_ids || []).map(String)
});

const mappingStatus = (mapping, draft, recipientGroups, users) => {
    if (draft.receipt_request_exempt) return { kind: 'exempt', label: 'Exempt' };
    if (draft.recipient_group_id) {
        const group = recipientGroups.find((candidate) => String(candidate.id) === draft.recipient_group_id);
        return { kind: 'group', label: group ? `${group.name} (${group.member_ids?.length || 0})` : 'Recipient group' };
    }
    if (draft.user_id) {
        const user = users.find((candidate) => String(candidate.id) === draft.user_id);
        return { kind: 'user', label: user?.name || 'CaseDB user' };
    }
    return { kind: 'unmapped', label: 'No match' };
};

const statusIcon = (kind) => {
    if (kind === 'group') return <Users size={13} />;
    if (kind === 'user') return <UserRound size={13} />;
    if (kind === 'exempt') return <Ban size={13} />;
    return null;
};

export const CardholderMappingsModal = ({
    mappings,
    users,
    recipientGroups,
    loading,
    uploading,
    savingMappingId,
    savingRecipientGroupId,
    onSaveMapping,
    onSaveRecipientGroup,
    onUpload,
    onClose,
}) => {
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [selectedMappingId, setSelectedMappingId] = useState(null);
    const [mappingDrafts, setMappingDrafts] = useState({});
    const [groupDrafts, setGroupDrafts] = useState({});
    const [selectedGroupId, setSelectedGroupId] = useState(null);

    const recipientOptions = useMemo(() => users
        .filter((user) => user?.id && user?.access_level !== 'no access')
        .map((user) => ({ value: String(user.id), label: user.email ? `${user.name} (${user.email})` : user.name || `User ${user.id}` })), [users]);
    const groupOptions = useMemo(() => recipientGroups
        .map((group) => ({ value: String(group.id), label: `${group.name} (${group.member_ids?.length || 0} member${(group.member_ids?.length || 0) === 1 ? '' : 's'})` })), [recipientGroups]);

    const draftFor = (mapping) => mappingDrafts[String(mapping.id)] || draftFromMapping(mapping);
    const updateDraft = (mapping, patch) => setMappingDrafts((current) => ({
        ...current,
        [String(mapping.id)]: { ...draftFor(mapping), ...patch }
    }));

    const groupDraftFor = (group) => groupDrafts[String(group.id)] || draftFromGroup(group);
    const updateGroupDraft = (group, patch) => setGroupDrafts((current) => ({
        ...current,
        [String(group.id)]: { ...groupDraftFor(group), ...patch }
    }));

    const statuses = useMemo(() => Object.fromEntries(mappings.map((mapping) => [
        String(mapping.id),
        mappingStatus(mapping, draftFor(mapping), recipientGroups, users)
    ])), [mappings, mappingDrafts, recipientGroups, users]);

    const counts = useMemo(() => {
        const next = { all: mappings.length, unmapped: 0, user: 0, group: 0, exempt: 0 };
        Object.values(statuses).forEach((status) => {
            if (status.kind === 'unmapped') next.unmapped += 1;
            if (status.kind === 'user') next.user += 1;
            if (status.kind === 'group') next.group += 1;
            if (status.kind === 'exempt') next.exempt += 1;
        });
        return next;
    }, [mappings, statuses]);

    const visibleMappings = useMemo(() => {
        const term = search.trim().toLowerCase();
        return mappings.filter((mapping) => {
            const status = statuses[String(mapping.id)] || { kind: 'unmapped', label: '' };
            if (filter !== 'all' && status.kind !== filter) return false;
            if (term === '') return true;
            return [mapping.configured_name, mapping.card_last_four, status.label]
                .some((value) => String(value || '').toLowerCase().includes(term));
        });
    }, [mappings, statuses, search, filter]);

    useEffect(() => {
        if (selectedMappingId && mappings.some((mapping) => String(mapping.id) === String(selectedMappingId))) return;
        setSelectedMappingId(mappings.length > 0 ? String(mappings[0].id) : null);
    }, [mappings, selectedMappingId]);

    const selectedMapping = mappings.find((mapping) => String(mapping.id) === String(selectedMappingId)) || null;
    const selectedDraft = selectedMapping ? draftFor(selectedMapping) : null;
    const selectedStatus = selectedMapping ? statuses[String(selectedMapping.id)] : null;
    const selectedMode = selectedDraft?.recipient_group_id ? 'group' : selectedDraft?.user_id ? 'user' : 'none';
    const selectedGroup = recipientGroups.find((group) => String(group.id) === String(selectedGroupId)) || null;
    const newGroupDraft = groupDrafts.new || { name: '', member_ids: [] };

    const saveSelectedMapping = async () => {
        if (!selectedMapping || !selectedDraft) return;
        await onSaveMapping(selectedMapping, selectedDraft);
    };

    const saveGroup = async (groupId, draft) => {
        const saved = await onSaveRecipientGroup(groupId, draft);
        if (saved && groupId === 'new') {
            setGroupDrafts((current) => ({ ...current, new: { name: '', member_ids: [] } }));
        }
    };

    const memberEditor = (groupId, draft, onDraftChange) => (
        <div className='receipt-mapping-group-members'>
            <SearchSelect
                value={null}
                onChange={(userId) => {
                    const memberId = String(userId || '');
                    if (memberId && !draft.member_ids.includes(memberId)) {
                        onDraftChange({ member_ids: [...draft.member_ids, memberId] });
                    }
                }}
                options={recipientOptions.filter((option) => !draft.member_ids.includes(option.value))}
                placeholder={loading ? 'Loading users...' : 'Search and add member'}
                disabled={loading || savingRecipientGroupId === groupId}
                minMatchScore={0.55}
                portal
            />
            <div className='receipt-mapping-member-chips'>
                {draft.member_ids.length === 0 && <span className='subtext'>No members yet.</span>}
                {draft.member_ids.map((memberId) => {
                    const member = recipientOptions.find((option) => option.value === memberId);
                    return (
                        <span key={memberId} className='receipt-mapping-member-chip'>
                            {member?.label || `User ${memberId}`}
                            <button
                                type='button'
                                onClick={() => onDraftChange({ member_ids: draft.member_ids.filter((id) => id !== memberId) })}
                                title={`Remove ${member?.label || `user ${memberId}`}`}
                            >
                                ×
                            </button>
                        </span>
                    );
                })}
            </div>
        </div>
    );

    return (
        <Modal
            wider
            title='Cardholder Mappings'
            onClose={onClose}
            header={
                <div className='modal-header-actions receipt-mapping-header-actions'>
                    <label className='action alt receipt-review-upload'>
                        <Upload size={16} /> Upload Card Map
                        <input type='file' accept='.csv,text/csv' onChange={onUpload} disabled={Boolean(uploading)} />
                    </label>
                    <button className='action alt' onClick={onClose}>Close</button>
                </div>
            }
        >
            <div className='modal-content-wrapper receipt-mapping-modal'>
                <aside className='receipt-mapping-list-panel'>
                    <div className='receipt-mapping-search'>
                        <Search size={15} />
                        <input
                            type='text'
                            value={search}
                            className='receipt-mapping-search-input'
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder='Search cards or cardholders'
                            aria-label='Search cardholder mappings'
                        />
                    </div>
                    <div className='receipt-mapping-filters'>
                        {[['all', 'All', counts.all], ['unmapped', 'Unmapped', counts.unmapped], ['user', 'Users', counts.user], ['group', 'Groups', counts.group], ['exempt', 'Exempt', counts.exempt]].map(([value, label, count]) => (
                            <button
                                key={value}
                                type='button'
                                className={`receipt-mapping-filter ${filter === value ? 'active' : ''}`}
                                onClick={() => setFilter(value)}
                            >
                                {label} <em>{count}</em>
                            </button>
                        ))}
                    </div>
                    <div className='receipt-mapping-card-list' role='listbox' aria-label='Cardholder mappings'>
                        {loading ? (
                            <div className='receipt-mapping-list-empty'><Loader2 size={16} className='spin' /> Loading mappings...</div>
                        ) : visibleMappings.length === 0 ? (
                            <div className='receipt-mapping-list-empty'>No cards match this view.</div>
                        ) : visibleMappings.map((mapping) => {
                            const status = statuses[String(mapping.id)];
                            return (
                                <button
                                    key={mapping.id}
                                    type='button'
                                    role='option'
                                    aria-selected={String(mapping.id) === String(selectedMappingId)}
                                    className={`receipt-mapping-card-item ${String(mapping.id) === String(selectedMappingId) ? 'selected' : ''}`}
                                    onClick={() => setSelectedMappingId(String(mapping.id))}
                                >
                                    <span className='receipt-mapping-card-line'>
                                        <strong>•••• {mapping.card_last_four}</strong>
                                        <span className={`receipt-mapping-pill ${status.kind}`}>{statusIcon(status.kind)}{status.label}</span>
                                    </span>
                                    <span className='receipt-mapping-card-name'>{mapping.configured_name}</span>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                <main className='receipt-mapping-form-panel'>
                    {!selectedMapping ? (
                        <section className='receipt-mapping-summary-card'>
                            <h3>No cardholder mappings yet</h3>
                            <p className='subtext'>Upload a card map CSV to create mappings, then assign each card to a CaseDB user or recipient group.</p>
                        </section>
                    ) : (
                        <>
                            <section className='receipt-mapping-summary-card'>
                                <div className='receipt-mapping-summary-topline'>
                                    <span className='receipt-mapping-summary-card-number'><CreditCard size={15} /> •••• {selectedMapping.card_last_four}</span>
                                    <span className={`receipt-mapping-pill ${selectedStatus.kind}`}>{statusIcon(selectedStatus.kind)}{selectedStatus.label}</span>
                                </div>
                                <h3>{selectedMapping.configured_name}</h3>
                                <div className='receipt-mapping-summary-meta subtext'>
                                    {selectedDraft.receipt_request_exempt
                                        ? 'Receipt requests are skipped for this card.'
                                        : selectedMode === 'group'
                                            ? 'Every group member receives a separate receipt request for this card.'
                                            : selectedMode === 'user'
                                                ? 'Receipt requests for this card go to one CaseDB user.'
                                                : 'Unassigned transactions for this card stay in the Unmapped filter.'}
                                </div>
                            </section>

                            <section className='receipt-mapping-section'>
                                <div className='receipt-mapping-section-title'>Recipient</div>
                                <div className='receipt-mapping-picker-grid'>
                                    <div className='form-group receipt-mapping-picker'>
                                        <label className='subtext'><UserRound size={13} /> Individual user</label>
                                        <SearchSelect
                                            value={selectedDraft.user_id || null}
                                            onChange={(userId) => updateDraft(selectedMapping, { user_id: userId ? String(userId) : '', recipient_group_id: '' })}
                                            options={recipientOptions}
                                            placeholder={loading ? 'Loading users...' : 'Search CaseDB users'}
                                            disabled={loading}
                                            minMatchScore={0.55}
                                            portal
                                        />
                                    </div>
                                    <div className='form-group receipt-mapping-picker'>
                                        <label className='subtext'><Users size={13} /> Recipient group</label>
                                        <SearchSelect
                                            value={selectedDraft.recipient_group_id || null}
                                            onChange={(groupId) => updateDraft(selectedMapping, { recipient_group_id: groupId ? String(groupId) : '', user_id: '' })}
                                            options={groupOptions}
                                            placeholder={groupOptions.length === 0 ? 'No recipient groups yet' : 'Search recipient groups'}
                                            disabled={loading || groupOptions.length === 0}
                                            minMatchScore={0.55}
                                            portal
                                        />
                                    </div>
                                </div>
                                <div className='receipt-mapping-picker-hint subtext'>
                                    {selectedMode === 'none'
                                        ? 'Pick one: assigning a user clears the group and vice versa.'
                                        : (
                                            <button type='button' className='receipt-mapping-clear' onClick={() => updateDraft(selectedMapping, { user_id: '', recipient_group_id: '' })}>
                                                Clear recipient (set to No match)
                                            </button>
                                        )}
                                </div>
                            </section>

                            <section className='receipt-mapping-section'>
                                <div className='receipt-mapping-section-title'>Options</div>
                                <div className='receipt-mapping-options'>
                                    <Checkbox
                                        label='Exempt from receipt requests'
                                        value={Boolean(selectedDraft.receipt_request_exempt)}
                                        onChange={(checked) => updateDraft(selectedMapping, { receipt_request_exempt: checked })}
                                    />
                                    <Checkbox
                                        label='Apply recipient to pending items for this card'
                                        value={Boolean(selectedDraft.apply_to_existing)}
                                        onChange={(checked) => updateDraft(selectedMapping, { apply_to_existing: checked })}
                                    />
                                </div>
                                <button
                                    type='button'
                                    className='action receipt-mapping-save'
                                    onClick={saveSelectedMapping}
                                    disabled={loading || savingMappingId === String(selectedMapping.id)}
                                >
                                    {savingMappingId === String(selectedMapping.id) ? <Loader2 size={16} className='spin' /> : <Save size={16} />}
                                    Save Mapping
                                </button>
                            </section>
                        </>
                    )}

                    <section className='receipt-mapping-section'>
                        <div className='receipt-mapping-section-title receipt-mapping-groups-title'>
                            <span>Recipient Groups</span>
                            <span className='subtext'>Every member receives a separate receipt request.</span>
                        </div>
                        <div className='receipt-mapping-group-tabs'>
                            {recipientGroups.map((group) => (
                                <button
                                    key={group.id}
                                    type='button'
                                    className={`receipt-mapping-group-tab ${String(group.id) === String(selectedGroupId) ? 'active' : ''}`}
                                    onClick={() => setSelectedGroupId(String(group.id) === String(selectedGroupId) ? null : String(group.id))}
                                >
                                    <Users size={13} /> {group.name} <em>{group.member_ids?.length || 0}</em>
                                </button>
                            ))}
                            <button
                                type='button'
                                className={`receipt-mapping-group-tab ${selectedGroupId === 'new' ? 'active' : ''}`}
                                onClick={() => setSelectedGroupId(selectedGroupId === 'new' ? null : 'new')}
                            >
                                + New group
                            </button>
                        </div>
                        {selectedGroupId === 'new' && (
                            <div className='receipt-mapping-group-editor'>
                                <div className='form-group nm'>
                                    <label className='subtext'>Group name</label>
                                    <input
                                        type='text'
                                        value={newGroupDraft.name}
                                        onChange={(event) => setGroupDrafts((current) => ({ ...current, new: { ...newGroupDraft, name: event.target.value } }))}
                                        placeholder='e.g. Marketing card users'
                                    />
                                </div>
                                {memberEditor('new', newGroupDraft, (patch) => setGroupDrafts((current) => ({ ...current, new: { ...newGroupDraft, ...patch } })))}
                                <button
                                    type='button'
                                    className='action alt receipt-mapping-group-save'
                                    onClick={() => saveGroup('new', newGroupDraft)}
                                    disabled={savingRecipientGroupId === 'new' || loading || !newGroupDraft.name.trim() || newGroupDraft.member_ids.length === 0}
                                >
                                    {savingRecipientGroupId === 'new' ? <Loader2 size={16} className='spin' /> : <Save size={16} />}
                                    Create Group
                                </button>
                            </div>
                        )}
                        {selectedGroup && selectedGroupId !== 'new' && (() => {
                            const groupDraft = groupDraftFor(selectedGroup);
                            return (
                                <div className='receipt-mapping-group-editor'>
                                    <div className='form-group'>
                                        <label className='subtext'>Group name</label>
                                        <input
                                            type='text'
                                            value={groupDraft.name}
                                            onChange={(event) => updateGroupDraft(selectedGroup, { name: event.target.value })}
                                        />
                                    </div>
                                    {memberEditor(String(selectedGroup.id), groupDraft, (patch) => updateGroupDraft(selectedGroup, patch))}
                                    <button
                                        type='button'
                                        className='action alt receipt-mapping-group-save'
                                        onClick={() => saveGroup(String(selectedGroup.id), groupDraft)}
                                        disabled={savingRecipientGroupId === String(selectedGroup.id) || loading || !groupDraft.name.trim() || groupDraft.member_ids.length === 0}
                                    >
                                        {savingRecipientGroupId === String(selectedGroup.id) ? <Loader2 size={16} className='spin' /> : <Save size={16} />}
                                        Save Group
                                    </button>
                                </div>
                            );
                        })()}
                    </section>
                </main>
            </div>
        </Modal>
    );
};

export default CardholderMappingsModal;
